import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_TYPES = [
  { id: 'invoice',        label: 'Customer invoice',       desc: 'Emailed to customer, downloaded from My Orders' },
  { id: 'receipt',        label: 'Order receipt',          desc: 'Compact paid-in-full proof of purchase' },
  { id: 'admin_report',   label: 'Admin orders report',    desc: 'Multi-order summary for internal accounting' },
  { id: 'driver_manifest',label: 'Driver delivery manifest', desc: 'Per-driver delivery list with signature lines' },
  { id: 'customer_card',  label: 'Customer card',          desc: 'A6 card emailed to new customers (barcode + how-to)' },
  { id: 'driver_card',    label: 'Driver onboarding card', desc: 'A6 QR card for new driver sign-up' },
];

// Per-doc visibility toggles — let the admin hide elements that don't
// apply to their card (e.g. no "VAT line" on a customer card). Keeping the
// list per-doc keeps the UI honest — admins only see toggles that work.
const VISIBILITY_BY_DOC = {
  invoice:         ['show_logo','show_customer_number','show_barcode','show_notes','show_vat_row','show_payment_terms','show_signatures'],
  receipt:         ['show_logo','show_customer_number','show_barcode','show_notes','show_vat_row','show_payment_terms','show_signatures'],
  admin_report:    ['show_logo','show_notes'],
  driver_manifest: ['show_logo','show_signatures','show_notes'],
  customer_card:   ['show_logo','show_business_name','show_barcode','show_instructions'],
  driver_card:     ['show_logo','show_url','show_instructions'],
};
const VISIBILITY_LABELS = {
  show_logo:            'Show brand logo',
  show_customer_number: 'Show customer number',
  show_barcode:         'Show scannable barcode',
  show_notes:           'Show notes section',
  show_vat_row:         'Show VAT line',
  show_payment_terms:   'Show payment terms',
  show_signatures:      'Show signature lines',
  show_business_name:   'Show business name',
  show_instructions:    'Show "how to use" instructions',
  show_url:             'Show signup URL text',
};

const LAYOUT_FIELDS = [
  ['margin_top',    'Margin top (mm)'],
  ['margin_right',  'Margin right (mm)'],
  ['margin_bottom', 'Margin bottom (mm)'],
  ['margin_left',   'Margin left (mm)'],
];
const TYPO_FIELDS = [
  ['font_size_h1',    'Heading size (pt)'],
  ['font_size_body',  'Body text size (pt)'],
  ['font_size_small', 'Small text size (pt)'],
];
const VISIBILITY_FIELDS_DEFAULT = [
  ['show_logo',           'Show brand logo'],
  ['show_customer_number','Show customer number'],
  ['show_barcode',        'Show scannable barcode'],
  ['show_notes',          'Show notes section'],
  ['show_vat_row',        'Show VAT line'],
  ['show_payment_terms',  'Show payment terms'],
  ['show_signatures',     'Show signature lines'],
];

/**
 * DocumentsPanel — admin UI for customizing every generated PDF (invoice,
 * receipt, admin orders report, driver manifest). Stores its state in the
 * parent `form.pdf_settings[doc_type]` object so it saves with the rest of
 * Site Settings, and offers a Preview Sample PDF button per doc type.
 */
const DocumentsPanel = ({ form, update, authHeaders }) => {
  const [docType, setDocType] = useState('invoice');
  const [defaults, setDefaults] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/pdf-defaults`, { headers: authHeaders })
      .then((r) => setDefaults(r.data))
      .catch(() => setDefaults(null));
  }, [authHeaders]);

  // Resolve effective value: admin override → factory default
  const effective = (key) => {
    const o = form?.pdf_settings?.[docType] ?? {};
    if (Object.prototype.hasOwnProperty.call(o, key)) return o[key];
    return defaults?.[docType]?.[key];
  };

  // Patch one field of the current doc type
  const setField = (key, value) => {
    const next = { ...(form.pdf_settings || {}) };
    next[docType] = { ...(next[docType] || {}), [key]: value };
    update({ pdf_settings: next });
  };

  const resetCurrent = () => {
    if (!window.confirm(`Reset all "${docType}" PDF customisations to factory defaults?`)) return;
    const next = { ...(form.pdf_settings || {}) };
    next[docType] = {};
    update({ pdf_settings: next });
  };

  const downloadPreview = async () => {
    setPreviewing(true);
    try {
      // Save the latest overrides before previewing so the preview reflects
      // unsaved edits too.
      await axios.put(
        `${API}/admin/settings`,
        { ...form },
        { headers: authHeaders },
      );
      const res = await axios.get(`${API}/admin/pdf-preview/${docType}`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      // toast handled at parent level
    } finally {
      setPreviewing(false);
    }
  };

  if (!defaults) {
    return <div className="text-sm text-neutral-500">Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid="documents-panel">
      {/* Doc-type tabs */}
      <div className="flex flex-wrap gap-2">
        {DOC_TYPES.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setDocType(d.id)}
            className={`px-4 h-10 rounded-full text-sm font-semibold transition border ${
              docType === d.id
                ? 'bg-[#0078D4] text-white border-[#0078D4]'
                : 'bg-white text-slate-700 border-slate-200 hover:border-[#0078D4]'
            }`}
            data-testid={`doc-tab-${d.id}`}
          >
            <FileText className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
            {d.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-neutral-500">
        {DOC_TYPES.find((d) => d.id === docType)?.desc}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={downloadPreview}
          disabled={previewing}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="doc-preview-btn"
        >
          <Download className="w-4 h-4 mr-1.5" />
          {previewing ? 'Generating…' : 'Preview sample PDF'}
        </Button>
        <Button
          type="button"
          onClick={resetCurrent}
          variant="outline"
          className="rounded-full"
          data-testid="doc-reset-btn"
        >
          <RotateCcw className="w-4 h-4 mr-1.5" /> Reset this document
        </Button>
      </div>

      {/* Header / Title */}
      <Group title="Header & title">
        <FieldText
          label="Document title (printed in the top-right)"
          value={effective('doc_title') || ''}
          onChange={(v) => setField('doc_title', v)}
          placeholder="Tax Invoice"
          testId="doc-title"
        />
        <FieldText
          label="Eyebrow line (blue text above the title)"
          value={effective('header_eyebrow') || ''}
          onChange={(v) => setField('header_eyebrow', v)}
          placeholder="Mohammed Riaz & Partner LLC"
          testId="doc-eyebrow"
        />
      </Group>

      {/* Footer */}
      <Group title="Footer">
        <FieldTextArea
          label="Footer text (printed at the bottom of every page)"
          value={effective('footer_text') || ''}
          onChange={(v) => setField('footer_text', v)}
          placeholder="Thank you for your business."
          testId="doc-footer"
        />
      </Group>

      {/* Layout */}
      <Group title="Page margins">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {LAYOUT_FIELDS.map(([k, label]) => (
            <FieldNumber
              key={k}
              label={label}
              value={effective(k)}
              onChange={(v) => setField(k, v)}
              testId={`doc-${k}`}
              min={0}
              max={50}
            />
          ))}
        </div>
      </Group>

      {/* Typography */}
      <Group title="Typography">
        <div className="grid grid-cols-3 gap-3">
          {TYPO_FIELDS.map(([k, label]) => (
            <FieldNumber
              key={k}
              label={label}
              value={effective(k)}
              onChange={(v) => setField(k, v)}
              testId={`doc-${k}`}
              min={6}
              max={48}
            />
          ))}
        </div>
        <div>
          <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Font family</label>
          <select
            value={effective('font_family') || 'Helvetica'}
            onChange={(e) => setField('font_family', e.target.value)}
            className="mt-1 h-11 w-full rounded-xl border border-neutral-300 px-3 text-sm"
            data-testid="doc-font-family"
          >
            <option>Helvetica</option>
            <option>Times</option>
            <option>Courier</option>
          </select>
        </div>
      </Group>

      {/* Branding */}
      <Group title="Brand colors & logo">
        <div className="grid grid-cols-3 gap-3">
          <FieldColor label="Accent color" value={effective('accent_color')} onChange={(v) => setField('accent_color', v)} testId="doc-accent" />
          <FieldColor label="Text color"  value={effective('text_color')}  onChange={(v) => setField('text_color', v)}  testId="doc-text" />
          <FieldColor label="Muted color" value={effective('muted_color')} onChange={(v) => setField('muted_color', v)} testId="doc-muted" />
        </div>
        <FieldNumber
          label="Logo max width (mm)"
          value={effective('logo_max_width_mm')}
          onChange={(v) => setField('logo_max_width_mm', v)}
          testId="doc-logo-w"
          min={10} max={80}
        />
        <div>
          <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Logo position</label>
          <select
            value={effective('logo_position') || 'top-left'}
            onChange={(e) => setField('logo_position', e.target.value)}
            className="mt-1 h-11 w-full rounded-xl border border-neutral-300 px-3 text-sm"
            data-testid="doc-logo-position"
          >
            <option value="top-left">Top left</option>
            <option value="top-right">Top right</option>
            <option value="top-center">Top center</option>
          </select>
        </div>
      </Group>

      {/* Section visibility — list adapts to the currently selected doc */}
      <Group title="Section visibility">
        <div className="grid sm:grid-cols-2 gap-2">
          {(VISIBILITY_BY_DOC[docType] || VISIBILITY_FIELDS_DEFAULT.map(([k]) => k)).map((k) => (
            <FieldSwitch
              key={k}
              label={VISIBILITY_LABELS[k] || k}
              value={effective(k) !== false}
              onChange={(v) => setField(k, v)}
              testId={`doc-${k}`}
            />
          ))}
        </div>
      </Group>

      {/* Watermark */}
      <Group title="Watermark">
        <FieldText
          label="Watermark text (leave empty to disable)"
          value={effective('watermark_text') || ''}
          onChange={(v) => setField('watermark_text', v)}
          placeholder="e.g. DRAFT, DUPLICATE, PAID"
          testId="doc-watermark"
        />
        <FieldColor
          label="Watermark color (lighter shades print best)"
          value={effective('watermark_color')}
          onChange={(v) => setField('watermark_color', v)}
          testId="doc-watermark-color"
        />
      </Group>

      {/* Cards (barcode + QR resize) — applies to standalone cards, not the
          per-doc PDFs above. Lives here for discoverability. */}
      <CardsSection form={form} update={update} authHeaders={authHeaders} />

      {/* Favicons — admin can change colors + labels for both browser tab
          icons. PNG Home Screen icons re-render via the "Regenerate" button. */}
      <FaviconsSection form={form} update={update} authHeaders={authHeaders} />
    </div>
  );
};

const CardsSection = ({ form, update, authHeaders }) => {
  const [meta, setMeta] = React.useState(null);
  React.useEffect(() => {
    axios
      .get(`${API}/admin/card-defaults`, { headers: authHeaders })
      .then((r) => setMeta(r.data))
      .catch(() => setMeta(null));
  }, [authHeaders]);

  if (!meta) return null;
  const cardKeys = Object.keys(meta.defaults || {});

  const eff = (group, key) => {
    const o = form?.card_settings?.[group] ?? {};
    if (Object.prototype.hasOwnProperty.call(o, key)) return o[key];
    return meta.defaults?.[group]?.[key];
  };

  const setField = (group, key, value) => {
    const next = { ...(form.card_settings || {}) };
    next[group] = { ...(next[group] || {}), [key]: value };
    update({ card_settings: next });
  };

  const resetGroup = (group) => {
    if (!window.confirm(`Reset "${group.replace('_', ' ')}" sizes to factory defaults?`)) return;
    const next = { ...(form.card_settings || {}) };
    next[group] = {};
    update({ card_settings: next });
  };

  const labels = {
    customer_card: 'Customer card (Code39 barcode)',
    driver_card:   'Driver onboarding card (QR code)',
  };
  const fieldLabels = {
    barcode_height_mm: 'Barcode height (mm)',
    barcode_width_mm:  'Barcode bar width (mm)',
    qr_size_px:        'QR code size (px)',
  };

  return (
    <div className="rounded-2xl border border-[#0078D4]/30 bg-[#DEECF9]/20 p-4 space-y-4" data-testid="card-resize-section">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-[#0078D4]">Card barcode & QR sizes</div>
        <p className="text-xs text-neutral-600 mt-1">
          Resize the barcode on the printed/emailed Customer card and the QR on the Driver onboarding card. These apply across the storefront, admin panel, and emailed PDFs.
        </p>
      </div>
      {cardKeys.map((group) => {
        const fields = meta.defaults[group];
        const ranges = meta.ranges?.[group] || {};
        return (
          <div key={group} className="rounded-xl border border-neutral-200 bg-white p-3 space-y-3" data-testid={`card-group-${group}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#0F172A]">{labels[group] || group}</div>
              <button
                type="button"
                onClick={() => resetGroup(group)}
                className="text-[11px] font-semibold text-neutral-500 hover:text-[#0078D4]"
                data-testid={`card-reset-${group}`}
              >
                Reset
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.keys(fields).map((k) => {
                const r = ranges[k] || { min: 0, max: 1000, step: 1 };
                const value = eff(group, k);
                return (
                  <div key={k}>
                    <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">
                      {fieldLabels[k] || k}
                      <span className="ml-2 font-mono text-neutral-400">({value ?? '—'})</span>
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="range"
                        min={r.min} max={r.max} step={r.step}
                        value={Number(value) || r.min}
                        onChange={(e) => setField(group, k, Number(e.target.value))}
                        className="flex-1 accent-[#0078D4]"
                        data-testid={`card-${group}-${k}-range`}
                      />
                      <Input
                        type="number"
                        value={value ?? ''}
                        onChange={(e) => setField(group, k, e.target.value === '' ? null : Number(e.target.value))}
                        min={r.min} max={r.max} step={r.step}
                        className="w-24 h-9"
                        data-testid={`card-${group}-${k}`}
                      />
                    </div>
                    <div className="text-[10px] text-neutral-400 mt-1">Range: {r.min} – {r.max}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Group = ({ title, children }) => (
  <div className="rounded-2xl border border-neutral-200 bg-white p-4 space-y-3">
    <div className="text-xs font-bold uppercase tracking-widest text-neutral-500">{title}</div>
    {children}
  </div>
);

const FieldText = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">{label}</label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 h-11" data-testid={testId} />
  </div>
);

const FieldTextArea = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">{label}</label>
    <Textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" data-testid={testId} />
  </div>
);

const FieldNumber = ({ label, value, onChange, testId, min, max }) => (
  <div>
    <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">{label}</label>
    <Input
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      min={min}
      max={max}
      className="mt-1 h-11"
      data-testid={testId}
    />
  </div>
);

const FieldColor = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">{label}</label>
    <div className="mt-1 flex gap-2">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-12 rounded-lg border border-neutral-300 cursor-pointer"
        data-testid={`${testId}-picker`}
      />
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 flex-1 font-mono text-xs"
        placeholder="#0078D4"
        data-testid={testId}
      />
    </div>
  </div>
);

const FieldSwitch = ({ label, value, onChange, testId }) => (
  <label
    className={`flex items-center justify-between gap-3 p-3 rounded-xl border cursor-pointer transition ${
      value
        ? 'bg-[#DEECF9]/40 border-[#0078D4]/30'
        : 'bg-slate-50 border-slate-200 opacity-70'
    }`}
    data-testid={testId}
  >
    <span className="text-sm font-medium text-[#0F172A]">{label}</span>
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
    <div className="relative w-10 h-5 bg-slate-300 rounded-full peer-checked:bg-[#0078D4] transition shrink-0">
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition ${value ? 'translate-x-5' : 'translate-x-0'}`} />
    </div>
  </label>
);

const FAVICON_FIELDS = [
  // Labels
  { k: 'top_label',     label: 'Top label',            type: 'text',  max: 6,  group: 'Labels' },
  { k: 'bottom_label',  label: 'Bottom label',         type: 'text',  max: 12, group: 'Labels' },
  // Colors
  { k: 'bg_top',           label: 'Background — top',     type: 'color', group: 'Colors' },
  { k: 'bg_bottom',        label: 'Background — bottom',  type: 'color', group: 'Colors' },
  { k: 'text_color',       label: 'Text color (default)', type: 'color', group: 'Colors' },
  { k: 'top_text_color',   label: 'Top label color (empty = default)',    type: 'color-optional', group: 'Colors' },
  { k: 'bottom_text_color',label: 'Bottom label color (empty = default)', type: 'color-optional', group: 'Colors' },
  // Custom image — overrides text composition entirely when set.
  { k: 'custom_image_url', label: 'Upload custom icon (overrides text + colours)', type: 'image-upload', group: 'Custom image' },
  // Typography
  { k: 'font_family',       label: 'Font family',          type: 'font',   group: 'Typography' },
  { k: 'top_font_weight',   label: 'Top label — weight',   type: 'weight', group: 'Typography' },
  { k: 'top_font_size',     label: 'Top label — size',     type: 'number', min: 6, max: 48, step: 1, group: 'Typography' },
  { k: 'bottom_font_weight',label: 'Bottom label — weight',type: 'weight', group: 'Typography' },
  { k: 'bottom_font_size',  label: 'Bottom label — size',  type: 'number', min: 4, max: 24, step: 0.5, group: 'Typography' },
  { k: 'letter_spacing',    label: 'Bottom letter-spacing',type: 'number', min: 0, max: 4, step: 0.1, group: 'Typography' },
  // Divider
  { k: 'divider_color',     label: 'Divider color (empty = text color)', type: 'color-optional', group: 'Divider' },
  { k: 'divider_width_pct', label: 'Divider width (% of icon)', type: 'number', min: 10, max: 90, step: 1, group: 'Divider' },
  { k: 'divider_thickness', label: 'Divider thickness',         type: 'number', min: 0.5, max: 6, step: 0.1, group: 'Divider' },
  // Visibility
  { k: 'show_top',     label: 'Show top label',     type: 'switch', group: 'Visibility' },
  { k: 'show_divider', label: 'Show divider',       type: 'switch', group: 'Visibility' },
  { k: 'show_bottom',  label: 'Show bottom label',  type: 'switch', group: 'Visibility' },
  { k: 'show_gloss',   label: 'Show gloss highlight', type: 'switch', group: 'Visibility' },
];

const FAVICON_GROUPS = ['Labels', 'Colors', 'Custom image', 'Typography', 'Divider', 'Visibility'];

// Font stacks that mirror the backend's FAVICON_FONT_STACKS so the Live
// Preview matches what gets baked into the SVG / PNG at save time.
const FAVICON_FONT_STACKS = {
  'Segoe UI': "'Segoe UI', 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  Inter:      "'Inter', 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  Helvetica:  "'Helvetica Neue', Helvetica, Arial, sans-serif",
  Times:      "'Times New Roman', Times, serif",
  Courier:    "'Courier New', Courier, monospace",
  Display:    "'Playfair Display', 'Georgia', serif",
};

/**
 * LiveFaviconSvg
 *
 * Client-side SVG renderer. Mirrors the backend `_render_favicon_svg`
 * structure exactly so the preview is pixel-accurate to the saved icon —
 * but reads from in-progress `form.favicon_settings` instead of the
 * database. This is the difference between "edit a color and see it
 * change instantly" (good UX) vs. "edit a color and see no change until
 * you click save" (the bug the user hit and gave up on).
 */
const LiveFaviconSvg = ({ cfg, ...rest }) => {
  const topLabel = String(cfg.top_label ?? 'MR').slice(0, 6).toUpperCase() || 'MR';
  const botLabel = String(cfg.bottom_label ?? '').slice(0, 12).toUpperCase();
  const bgTop = String(cfg.bg_top ?? '#FF8A3D');
  const bgBot = String(cfg.bg_bottom ?? '#E96B1B');
  const text  = String(cfg.text_color ?? '#FFFFFF');
  const topText = (String(cfg.top_text_color ?? '').trim()) || text;
  const botText = (String(cfg.bottom_text_color ?? '').trim()) || text;
  const customImage = String(cfg.custom_image_url ?? '').trim();
  const spacing = Number(cfg.letter_spacing ?? 1.2);
  const fontStack = FAVICON_FONT_STACKS[String(cfg.font_family ?? 'Segoe UI')] || FAVICON_FONT_STACKS['Segoe UI'];
  const topFs = Number(cfg.top_font_size ?? 22);
  const botFs = Number(cfg.bottom_font_size ?? 8);
  const topW = Number(cfg.top_font_weight ?? 900);
  const botW = Number(cfg.bottom_font_weight ?? 800);
  const showTop = cfg.show_top !== false;
  const showDiv = cfg.show_divider !== false;
  const showBot = cfg.show_bottom !== false;
  const showGloss = cfg.show_gloss !== false;
  const divColor = (String(cfg.divider_color ?? '').trim()) || text;
  const divPct = Number(cfg.divider_width_pct ?? 31);
  const divH = Number(cfg.divider_thickness ?? 1.8);
  const divW = Math.max(2, Math.min(60, (64 * divPct) / 100));
  const divX = (64 - divW) / 2;

  return (
    <svg
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className="w-32 h-32 rounded-2xl shadow-lg"
      {...rest}
    >
      {customImage ? (
        // Custom upload wins — render the image fitted to the rounded
        // square so admin sees the actual icon users will install.
        <>
          <defs>
            <clipPath id="lpfav-clip"><rect width="64" height="64" rx="14" /></clipPath>
          </defs>
          <rect width="64" height="64" rx="14" fill="#F1F5F9" />
          <image href={customImage} x="0" y="0" width="64" height="64" preserveAspectRatio="xMidYMid meet" clipPath="url(#lpfav-clip)" />
        </>
      ) : (
        <>
        <defs>
          <linearGradient id="lpfav-g" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor={bgTop} />
            <stop offset="1" stopColor={bgBot} />
          </linearGradient>
          {showGloss && (
            <radialGradient id="lpfav-gloss" cx="32" cy="-8" r="38" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.40" />
              <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
            </radialGradient>
          )}
        </defs>
        <rect width="64" height="64" rx="14" fill="url(#lpfav-g)" />
        {showGloss && <rect width="64" height="64" rx="14" fill="url(#lpfav-gloss)" />}
        {showTop && (
          <text
            x="32" y="27" textAnchor="middle" dominantBaseline="central"
            fontFamily={fontStack} fontWeight={topW} fontSize={topFs}
            fill={topText} letterSpacing="-0.5"
          >
            {topLabel}
          </text>
        )}
        {showDiv && (
          <rect
            x={divX.toFixed(2)} y="36" width={divW.toFixed(2)} height={divH}
            rx={Math.min(divH / 2, 2).toFixed(2)}
            fill={divColor} fillOpacity="0.9"
          />
        )}
        {showBot && botLabel && (
          <text
            x="32" y="47" textAnchor="middle" dominantBaseline="central"
            fontFamily={fontStack} fontWeight={botW} fontSize={botFs}
            fill={botText} letterSpacing={spacing}
          >
            {botLabel}
          </text>
        )}
        </>
      )}
    </svg>
  );
};

const FaviconsSection = ({ form, update, authHeaders }) => {
  const [meta, setMeta] = React.useState(null);
  const [active, setActive] = React.useState('storefront');
  const [bust, setBust] = React.useState(Date.now());
  const [regenerating, setRegenerating] = React.useState(false);
  const [regenMsg, setRegenMsg] = React.useState('');
  const [forceBusting, setForceBusting] = React.useState(false);

  React.useEffect(() => {
    axios
      .get(`${API}/admin/favicon-defaults`, { headers: authHeaders })
      .then((r) => setMeta(r.data))
      .catch(() => setMeta(null));
  }, [authHeaders]);

  if (!meta) return null;
  const portals = Object.keys(meta.defaults || {});

  const eff = (portal, key) => {
    const o = form?.favicon_settings?.[portal] ?? {};
    if (Object.prototype.hasOwnProperty.call(o, key) && o[key] !== '') return o[key];
    return meta.defaults?.[portal]?.[key];
  };

  const setField = (portal, key, value) => {
    const next = { ...(form.favicon_settings || {}) };
    next[portal] = { ...(next[portal] || {}), [key]: value };
    update({ favicon_settings: next });
    setBust(Date.now());
  };

  const resetPortal = (portal) => {
    if (!window.confirm(`Reset "${portal}" favicon to factory defaults?`)) return;
    const next = { ...(form.favicon_settings || {}) };
    next[portal] = {};
    update({ favicon_settings: next });
    setBust(Date.now());
  };

  const regeneratePngs = async () => {
    setRegenerating(true);
    setRegenMsg('');
    try {
      await axios.post(`${API}/admin/favicon/regenerate-pngs`, {}, { headers: authHeaders });
      setRegenMsg('Home Screen icons regenerated. Re-add the app to your Home Screen to pick up the new look.');
      setBust(Date.now());
    } catch (e) {
      setRegenMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  // "Force refresh installed icons" — bumps settings.updated_at to NOW
  // server-side, which rotates every favicon / manifest cache-bust URL
  // and forces every open browser tab + Home Screen PWA to re-fetch
  // the latest icon on the very next load. Useful when an end-user is
  // stuck on a stale cached icon and a fresh save would feel
  // unnecessary (no actual config change needed).
  const forceRefreshIcons = async () => {
    setForceBusting(true);
    setRegenMsg('');
    try {
      const r = await axios.post(`${API}/admin/favicon/force-refresh`, {}, { headers: authHeaders });
      const stamp = r?.data?.updated_at || new Date().toISOString();
      // Surface push delivery stats so the admin sees how many devices
      // got the broadcast (open tabs hot-swap their <link rel="icon">
      // in-place via SW postMessage; installed PWAs receive a silent
      // OS-tray notification and purge their own caches).
      const push = r?.data?.push || {};
      let pushNote = '';
      if (push.configured && (push.sent || push.failed)) {
        pushNote = ` · Push: ${push.sent || 0} delivered${push.failed ? `, ${push.failed} failed` : ''}.`;
      } else if (push.configured) {
        pushNote = ' · No active push subscribers.';
      }
      setRegenMsg(
        `Force-refresh sent (cache version: ${stamp}).${pushNote} Every browser tab will pick up the latest icon on its next reload; PWA Home Screen apps refresh on next launch.`,
      );
      setBust(Date.now());
    } catch (e) {
      setRegenMsg(`Force-refresh failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setForceBusting(false);
    }
  };

  const labels = {
    storefront: 'Storefront (estore.mrpco.net)',
    driver: 'Driver portal (/driver)',
    live_report: 'Live Stock Report (share links)',
    outstanding: 'Outstanding (share links)',
  };
  const portalCfg = portals.find((p) => p === active) || portals[0];

  return (
    <div className="rounded-2xl border border-[#0078D4]/30 bg-[#DEECF9]/20 p-4 space-y-4" data-testid="favicons-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#0078D4]">Favicons & app icons</div>
          <p className="text-xs text-neutral-600 mt-1 max-w-xl">
            Customise the browser-tab icon AND the PWA Home Screen icon for both portals. Browser tab updates instantly; for Home Screen icons click <b>Regenerate</b> below after saving.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={forceRefreshIcons}
            disabled={forceBusting || regenerating}
            className="rounded-full border-[#0078D4] text-[#0078D4] hover:bg-[#DEECF9] h-9 px-4"
            data-testid="favicon-force-refresh"
            title="Bumps the cache-bust on every dynamic favicon URL. Use after a brand refresh to force every viewer's tab to fetch the new icon on next reload."
          >
            {forceBusting ? 'Refreshing…' : 'Force refresh installed icons'}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={regeneratePngs}
            disabled={regenerating || forceBusting}
            className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white h-9 px-4"
            data-testid="favicon-regen-pngs"
          >
            {regenerating ? 'Regenerating…' : 'Regenerate Home Screen icons'}
          </Button>
        </div>
      </div>
      {regenMsg && (
        <div className="text-xs px-3 py-2 rounded-md bg-white border border-neutral-200 text-neutral-700">{regenMsg}</div>
      )}

      {/* Portal tabs */}
      <div className="inline-flex bg-white rounded-full p-1 border border-neutral-200">
        {portals.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setActive(p)}
            className={`px-3 h-8 rounded-full text-xs font-bold uppercase tracking-wide transition ${
              active === p ? 'bg-[#0078D4] text-white' : 'text-neutral-600 hover:text-[#0078D4]'
            }`}
            data-testid={`favicon-tab-${p}`}
          >
            {labels[p] || p}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4" data-testid={`favicon-fields-${portalCfg}`}>
          {FAVICON_GROUPS.map((grp) => {
            const fieldsInGroup = FAVICON_FIELDS.filter((f) => f.group === grp);
            if (!fieldsInGroup.length) return null;
            return (
              <div key={grp} className="rounded-xl border border-neutral-200 bg-white p-3 space-y-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{grp}</div>
                {fieldsInGroup.map((f) => {
                  const value = eff(portalCfg, f.k);
                  return (
                    <div key={f.k}>
                      <label className="text-xs font-semibold text-neutral-700">{f.label}</label>
                      {f.type === 'color' && (
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="color"
                            value={value || '#000000'}
                            onChange={(e) => setField(portalCfg, f.k, e.target.value.toUpperCase())}
                            className="w-12 h-10 rounded-md border border-neutral-300 cursor-pointer"
                            data-testid={`favicon-${portalCfg}-${f.k}-picker`}
                          />
                          <Input
                            value={value || ''}
                            onChange={(e) => setField(portalCfg, f.k, e.target.value.toUpperCase())}
                            className="flex-1 h-10 font-mono uppercase"
                            placeholder="#RRGGBB"
                            data-testid={`favicon-${portalCfg}-${f.k}`}
                          />
                        </div>
                      )}
                      {f.type === 'color-optional' && (
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="color"
                            value={(value && /^#[0-9A-F]{6}$/i.test(value)) ? value : '#000000'}
                            onChange={(e) => setField(portalCfg, f.k, e.target.value.toUpperCase())}
                            className="w-12 h-10 rounded-md border border-neutral-300 cursor-pointer"
                            data-testid={`favicon-${portalCfg}-${f.k}-picker`}
                          />
                          <Input
                            value={value || ''}
                            placeholder="leave empty = text color"
                            onChange={(e) => setField(portalCfg, f.k, e.target.value.toUpperCase())}
                            className="flex-1 h-10 font-mono uppercase"
                            data-testid={`favicon-${portalCfg}-${f.k}`}
                          />
                          {value && (
                            <button
                              type="button"
                              onClick={() => setField(portalCfg, f.k, '')}
                              className="h-10 px-2 text-xs text-neutral-500 hover:text-red-500"
                              data-testid={`favicon-${portalCfg}-${f.k}-clear`}
                              title="Clear override"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      )}
                      {f.type === 'image-upload' && (
                        <div className="mt-1 space-y-2">
                          {value ? (
                            <div className="flex items-center gap-2">
                              <div className="w-12 h-12 rounded-md border border-neutral-300 bg-neutral-50 flex items-center justify-center overflow-hidden">
                                <img src={value} alt="custom icon" className="max-w-full max-h-full" />
                              </div>
                              <div className="flex-1 min-w-0 text-xs font-mono text-neutral-500 truncate" title={value}>
                                {value.startsWith('data:') ? '(uploaded image)' : value}
                              </div>
                              <button
                                type="button"
                                onClick={() => setField(portalCfg, f.k, '')}
                                className="text-xs font-semibold text-red-500 hover:bg-red-50 px-2 py-1 rounded"
                                data-testid={`favicon-${portalCfg}-${f.k}-remove`}
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <label className="block">
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  // Square check (advisory only — we keep
                                  // the file regardless so admins can use
                                  // logos that aren't perfectly square).
                                  if (file.size > 5 * 1024 * 1024) {
                                    alert('Image too large (max 5 MB). Compress before uploading.');
                                    return;
                                  }
                                  const reader = new FileReader();
                                  reader.onload = () => setField(portalCfg, f.k, reader.result);
                                  reader.readAsDataURL(file);
                                }}
                                className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#DEECF9] file:text-[#0078D4] hover:file:bg-[#C7E0F4] cursor-pointer"
                                data-testid={`favicon-${portalCfg}-${f.k}-upload`}
                              />
                              <p className="text-[11px] text-neutral-500 mt-1">
                                PNG, JPEG, WebP or SVG. Square images work best. Max 5 MB. When set, this image replaces the generated text icon entirely.
                              </p>
                            </label>
                          )}
                        </div>
                      )}
                      {f.type === 'text' && (
                        <Input
                          value={value || ''}
                          maxLength={f.max}
                          onChange={(e) => setField(portalCfg, f.k, e.target.value)}
                          className="mt-1 h-10"
                          data-testid={`favicon-${portalCfg}-${f.k}`}
                        />
                      )}
                      {f.type === 'number' && (
                        <Input
                          type="number"
                          value={value ?? ''}
                          min={f.min} max={f.max} step={f.step}
                          onChange={(e) => setField(portalCfg, f.k, e.target.value === '' ? null : Number(e.target.value))}
                          className="mt-1 h-10"
                          data-testid={`favicon-${portalCfg}-${f.k}`}
                        />
                      )}
                      {f.type === 'font' && (
                        <select
                          value={value || ''}
                          onChange={(e) => setField(portalCfg, f.k, e.target.value)}
                          className="mt-1 h-10 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm"
                          data-testid={`favicon-${portalCfg}-${f.k}`}
                        >
                          {(meta.fonts || []).map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      )}
                      {f.type === 'weight' && (
                        <select
                          value={value ?? 900}
                          onChange={(e) => setField(portalCfg, f.k, Number(e.target.value))}
                          className="mt-1 h-10 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm"
                          data-testid={`favicon-${portalCfg}-${f.k}`}
                        >
                          {(meta.weights || [400, 600, 700, 800, 900]).map((w) => (
                            <option key={w} value={w}>{w}{w === 400 ? ' (Regular)' : w === 700 ? ' (Bold)' : w === 900 ? ' (Black)' : ''}</option>
                          ))}
                        </select>
                      )}
                      {f.type === 'switch' && (
                        <label className="mt-1 flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={value !== false}
                            onChange={(e) => setField(portalCfg, f.k, e.target.checked)}
                            className="w-4 h-4 accent-[#0078D4]"
                            data-testid={`favicon-${portalCfg}-${f.k}`}
                          />
                          <span className="text-sm text-neutral-700">{value !== false ? 'Visible' : 'Hidden'}</span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => resetPortal(portalCfg)}
            className="text-[11px] font-semibold text-neutral-500 hover:text-[#0078D4]"
            data-testid={`favicon-reset-${portalCfg}`}
          >
            Reset this portal to defaults
          </button>
        </div>

        <div className="flex flex-col items-center justify-center gap-3 p-4 rounded-xl bg-white border border-neutral-200 md:sticky md:top-4 md:self-start">
          <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Live preview</div>
          <LiveFaviconSvg
            cfg={Object.fromEntries(FAVICON_FIELDS.map((f) => [f.k, eff(portalCfg, f.k)]))}
            data-testid={`favicon-preview-${portalCfg}`}
          />
          <div className="text-[11px] text-neutral-500 text-center max-w-[200px]">
            Updates live as you type. Click <b>Save changes</b> at the bottom to apply for everyone.
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsPanel;
